import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Lock } from 'lucide-react';
import { isBetaActive } from '../../utils/betaMode';

// Locale-aware amount formatter. Currency symbol is supplied separately
// by the pricing payload, so we never prefix a raw currency code.
const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const PriceBlock = ({ symbol, tier, tone, testid }) => {
  if (!tier) return null;
  return (
    <div className={`mb-5 ${tone}`} data-testid={testid}>
      <div className="text-2xl font-serif font-bold leading-tight">
        {symbol}{fmt(tier.monthly)}
        <span className="text-base font-normal ml-1">/ month</span>
      </div>
      <div className="text-xs mt-1 opacity-80">
        or {symbol}{fmt(tier.annual)} / year &middot; Save 17%
      </div>
    </div>
  );
};

// "Available after Beta" — shown to anonymous visitors and to signed-in
// non-subscribers during the beta window.
const BetaLockedButton = ({ testid }) => (
  <button
    type="button"
    disabled
    data-testid={testid}
    className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-200 text-slate-500 font-semibold px-6 py-3 cursor-not-allowed"
  >
    <Lock className="w-4 h-4" />
    Available after Beta
  </button>
);

// Soft "Your Current Plan" badge — non-clickable status marker for the tier
// the signed-in user is already on.
const CurrentPlanBadge = ({ testid, tone }) => (
  <div
    data-testid={testid}
    className={`inline-flex items-center justify-center gap-2 rounded-full border-2 font-semibold px-6 py-3 ${tone}`}
  >
    <Check className="w-4 h-4" />
    Your Current Plan
  </div>
);

// Cross-tier action link. Routes signed-in subscribers to their Settings
// → Subscription tab, where the existing upgrade / downgrade flow lives.
const CrossTierLink = ({ testid, label, tone }) => (
  <Link
    to="/settings?tab=subscription"
    data-testid={testid}
    className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold px-6 py-3 transition ${tone}`}
  >
    {label}
    <ArrowRight className="w-4 h-4" />
  </Link>
);

// Session 283: signed-in existing subscribers (Sophia + all beta testers)
// no longer see "Available after Beta" on the tiers they already own.
// Instead they see "Your Current Plan" on their tier and a matching
// upgrade / downgrade link on the other tier. Anonymous visitors and
// signed-in non-subscribers still see the beta lock during the beta.
const PORTFOLIO_TIERS = new Set(['premium_portfolio', 'premium_continuity']);
const CONTINUITY_TIER = 'portfolio_continuity';

const ContinuityCta = ({ currentTier, betaActive, hasSession, onRequestInvitation }) => {
  if (currentTier === CONTINUITY_TIER) {
    return <CurrentPlanBadge testid="compare-continuity-current-plan" tone="border-teal-400 bg-white text-teal-800" />;
  }
  if (hasSession && PORTFOLIO_TIERS.has(currentTier)) {
    return <CrossTierLink
      testid="compare-continuity-downgrade-link"
      label="Switch to Continuity System"
      tone="bg-white border-2 border-teal-700 text-teal-800 hover:bg-teal-50"
    />;
  }
  if (betaActive) return <BetaLockedButton testid="compare-continuity-create-account-btn" />;
  return (
    <Link
      to="/login?tab=register"
      data-testid="compare-continuity-create-account-btn"
      className="inline-flex items-center justify-center gap-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold px-6 py-3 transition"
    >
      Create Account
      <ArrowRight className="w-4 h-4" />
    </Link>
  );
};

const PortfolioCta = ({ currentTier, betaActive, hasSession, onRequestInvitation }) => {
  if (hasSession && PORTFOLIO_TIERS.has(currentTier)) {
    return <CurrentPlanBadge testid="compare-premium-current-plan" tone="border-emerald-400 bg-white text-emerald-800" />;
  }
  if (currentTier === CONTINUITY_TIER) {
    return <CrossTierLink
      testid="compare-premium-upgrade-link"
      label="Upgrade to Portfolio Clarity"
      tone="bg-emerald-700 hover:bg-emerald-800 text-white"
    />;
  }
  if (betaActive) return <BetaLockedButton testid="compare-premium-request-invite-btn" />;
  return (
    <button
      type="button"
      onClick={onRequestInvitation}
      data-testid="compare-premium-request-invite-btn"
      className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-6 py-3 transition"
    >
      Request an Invitation
      <ArrowRight className="w-4 h-4" />
    </button>
  );
};

const CompareCta = ({ onRequestInvitation, pricing }) => {
  const betaActive = isBetaActive();
  const hasSession = !!pricing;
  const currentTier = pricing?.current_tier || null;
  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="compare-cta">
      <div className="rounded-2xl border border-teal-200 bg-teal-50/60 p-6 sm:p-8 flex flex-col">
        <h3 className="text-2xl font-serif font-bold text-teal-900 mb-2">Continuity System</h3>
        <p className="text-sm text-slate-700 mb-6 grow">
          A private record of every asset you own &mdash; clear for you today, and quietly ready for the people you trust if you&rsquo;re ever unavailable.
        </p>
        <PriceBlock symbol={pricing?.symbol} tier={pricing?.continuity}
                    tone="text-teal-900" testid="compare-continuity-price" />
        <ContinuityCta currentTier={currentTier} betaActive={betaActive} hasSession={hasSession} onRequestInvitation={onRequestInvitation} />
      </div>
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 sm:p-8 flex flex-col">
        <h3 className="text-2xl font-serif font-bold text-emerald-900 mb-2">Portfolio Clarity</h3>
        <p className="text-sm text-slate-700 mb-6 grow">
          Everything in Continuity System, plus the full analytical view &mdash; portfolio allocation, performance, tax data, net worth and portfolio sharing.
        </p>
        <PriceBlock symbol={pricing?.symbol} tier={pricing?.portfolio}
                    tone="text-emerald-900" testid="compare-premium-price" />
        <PortfolioCta currentTier={currentTier} betaActive={betaActive} hasSession={hasSession} onRequestInvitation={onRequestInvitation} />
      </div>
    </section>
  );
};

export default CompareCta;
